import type { ComponentType } from 'react'
import clsx from 'clsx'
import type { CaptureToken, ISODate } from '@app/shared'

import { BrowserPuzzle, Desktop, Phone, Tablet, type GlyphProps } from './icons'
import { agoPhrase, daysAgoOf, formatShortDateOf } from './labels'
import { tokenIsStale } from './model'
import { ActionButton } from './parts'

// ============================================================================
// CAPTURE TOKENS — one row per device that is allowed to write into LifeOS.
//
// THE DESTRUCTIVE ACTION GETS *MORE* PROMINENT AS THE TOKEN GOES STALE, which
// is the opposite of the usual instinct. A key nobody has used in three months
// is the one worth taking back, so its row greys down and its Revoke turns from
// an outline into solid ink: the quiet row is the dangerous one, and the design
// makes the danger the loudest thing in it.
//
// A REVOKED TOKEN IS REPORTED, NOT DELETED. It leaves the list and reappears as
// a footer line — "captures from it now fail loudly" — because a device silently
// vanishing from a security list is how you end up unsure whether you revoked it
// or it was never there.
// ============================================================================

const GLYPH: Record<CaptureToken['device'], ComponentType<GlyphProps>> = {
  phone: Phone,
  extension: BrowserPuzzle,
  tablet: Tablet,
  desktop: Desktop,
  pc_worker: Desktop,
  unknown: Desktop,
}

export function CaptureTokens({
  tokens,
  today,
  nowMinutes,
}: {
  tokens: CaptureToken[]
  today: ISODate
  nowMinutes: number
}) {
  const active = tokens.filter((t) => t.revokedAt === null)
  const revoked = tokens.filter((t) => t.revokedAt !== null)

  return (
    <div className="flex flex-1 flex-col rounded-card border border-line bg-surface px-[6px] pb-[10px] pt-[6px]">
      {active.length === 0 && (
        <p className="px-[13px] py-[11px] text-[11.5px] leading-[1.45] text-ink-3">
          No device can capture into LifeOS right now. Every token has been revoked.
        </p>
      )}

      {active.map((token, i) => (
        <TokenRow
          key={token.id}
          token={token}
          last={i === active.length - 1}
          today={today}
          nowMinutes={nowMinutes}
        />
      ))}

      {revoked.length > 0 && (
        <div className="mt-auto flex items-center gap-[9px] px-[13px] pb-1 pt-[10px]">
          <ActionButton
            label="New token"
            variant="dark"
            size="xs"
            waitingFor="Issuing a capture token needs the capture endpoint, which arrives with V5."
          />
          <p className="text-[10.5px] font-normal leading-[1.4] text-microcopy text-pretty">
            {revoked
              .map(
                (t) =>
                  `${t.label.split(' · ')[0]} token revoked ${formatShortDateOf(t.revokedAt!)} — captures from it now fail loudly`,
              )
              .join(' · ')}
          </p>
        </div>
      )}
    </div>
  )
}

function TokenRow({
  token,
  last,
  today,
  nowMinutes,
}: {
  token: CaptureToken
  last: boolean
  today: ISODate
  nowMinutes: number
}) {
  const stale = tokenIsStale(token, today)
  const Glyph = GLYPH[token.device]

  return (
    <div
      className={clsx(
        'flex items-center gap-3 px-[13px] pb-[10px] pt-[11px]',
        !last && 'border-b border-fill-2',
      )}
    >
      <Glyph size={15} strokeWidth={1.8} className={clsx('flex-none', stale ? 'text-faint-2' : 'text-muted')} />
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <div className={clsx('truncate text-[12px] font-medium leading-none', stale && 'text-muted')}>
          {token.label}
        </div>
        <div className="text-[10.5px] font-normal leading-none text-microcopy">
          {lastCaptureLine(token, today, nowMinutes)}
        </div>
      </div>
      <ActionButton
        label="Revoke"
        size="xs"
        variant={stale ? 'dark' : 'quiet'}
        waitingFor={`Revoking ${token.label} needs the capture endpoint, which arrives with V5.`}
      />
    </div>
  )
}

/**
 * 'last capture 41 min ago · 88 this month' · 'last capture Jun 25 · unused for
 * 83 days'. A stale token's line stops reporting throughput — the number that
 * matters about it is how long it has been sitting there unused.
 */
function lastCaptureLine(token: CaptureToken, today: ISODate, nowMinutes: number): string {
  if (!token.lastUsedAt) return 'never used'
  const days = daysAgoOf(token.lastUsedAt, today)
  if (tokenIsStale(token, today)) {
    return `last capture ${formatShortDateOf(token.lastUsedAt)} · unused for ${days} days`
  }
  return token.capturesThisMonth === null ? `last capture ${agoPhrase(token.lastUsedAt, today, nowMinutes)}` : `last capture ${agoPhrase(token.lastUsedAt, today, nowMinutes)} · ${token.capturesThisMonth} this month`
}
